import React, { useState, useEffect } from "react";
import { Button, Icon, Alert } from "tabler-react";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import Axios from "axios";
import { NotificationManager } from "react-notifications";
import { api_raiz } from "../../../config/config";

// ===================== COLUMNAS DEL TEMPLATE =====================
// Orden exacto del formulario
const COLUMNAS_OBLIGATORIAS = [
  "Tipo Colocación GPS",
  "Cliente",
  "Razón Social a Facturar",
  "Tipo Movimiento",
  "Corredor/Origen",
  "Estado",
  "Municipio",
  "Colonia",
  "Calle",
  "Número",
  "CP",
  "Localidad GPS",
  "Transportista",
  "Tipo Contenedor",
  "Tamaño Contenedor",
  "Cita Programada",
  "Cita Colocación GPS",
  "Contacto Monitoreo 1",
  "Contacto Monitoreo 2",
  "Contacto Línea Colocación 1",
  "Contacto Línea Colocación 2",
  "Modalidades Transporte",
  "Contenedor/Caja",
  "Referencia Facturación",
];

const COLUMNAS_OPCIONALES = [
  "Tecnología",
  "Colocación Desechable",
  "Coloca",
  "OV",
  "Modelo Tracto",
  "Marca Tractor",
  "Color Tractor",
  "Placa Tractor",
  "Económico Tracto",
  "Placa Remolque",
  "Económico Remolque",
  "Observaciones",
  "Nombre Operador",
  "Medio Contacto Operador",
  "Licencia",
  "Booking",
  "Contacto Monitoreo 3",
  "Contacto Línea Colocación 3",
  "Nota",
  "Cuenta Espejo URL",
  "Cuenta Espejo Usuario",
  "Cuenta Espejo Password",
  "PO/OP",
];

// Orden real en el Excel (como el formulario)
const TODAS_COLUMNAS = [
  "Tipo Colocación GPS",
  "Tecnología",
  "Colocación Desechable",
  "Coloca",
  "OV",
  "Cliente",
  "Razón Social a Facturar",
  "Tipo Movimiento",
  "Corredor/Origen",
  "Estado",
  "Municipio",
  "Colonia",
  "Calle",
  "Número",
  "CP",
  "Localidad GPS",
  "Transportista",
  "Tipo Contenedor",
  "Tamaño Contenedor",
  "Cita Programada",
  "Cita Colocación GPS",
  "Modelo Tracto",
  "Marca Tractor",
  "Color Tractor",
  "Placa Tractor",
  "Económico Tracto",
  "Placa Remolque",
  "Económico Remolque",
  "Observaciones",
  "Nombre Operador",
  "Medio Contacto Operador",
  "Licencia",
  "Booking",
  "Contacto Monitoreo 1",
  "Contacto Monitoreo 2",
  "Contacto Monitoreo 3",
  "Contacto Línea Colocación 1",
  "Contacto Línea Colocación 2",
  "Contacto Línea Colocación 3",
  "Nota",
  "Cuenta Espejo URL",
  "Cuenta Espejo Usuario",
  "Cuenta Espejo Password",
  "Modalidades Transporte",
  "Contenedor/Caja",
  "Referencia Facturación",
  "PO/OP",
];

const TECNOLOGIA_OPCIONES = ["4G Lite", "5G"];
const COLOCACION_DESECHABLE_OPCIONES = ["Dentro de la caja", "Dentro de la carga", "Fuera de la caja/contenedor"];
const COLOCA_OPCIONES = ["Coloca cliente", "Coloca Sky"];

// ===================== GENERACIÓN DE TEMPLATE CON EXCELJS (DROPDOWNS) =====================
async function generarTemplate(catalogos) {
  const wb = new ExcelJS.Workbook();

  // --- Hoja Catálogos (se crea primero para referenciarla) ---
  const wsCat = wb.addWorksheet("Catalogos", { state: "hidden" });
  const catColumnas = [
    { header: "Clientes", data: (catalogos.clientes || []).map(c => c.label || "") },
    { header: "Transportistas", data: (catalogos.transportistas || []).map(c => c.label || "") },
    { header: "Tipo Movimiento", data: (catalogos.movimientosTipo || []).map(c => c.label || "") },
    { header: "Corredor/Origen", data: (catalogos.corredores || []).map(c => c.label || "") },
    { header: "Localidad GPS", data: (catalogos.localidades || []).map(c => c.label || "") },
    { header: "Tamaño Contenedor", data: (catalogos.remolquesTipo || []).map(c => c.label || "") },
    { header: "Tipo Contenedor", data: (catalogos.contenedoresTipo || []).map(c => c.label || "") },
    { header: "Tipo Colocación GPS", data: (catalogos.gpsTipoColocaciones || []).map(c => c.label || "") },
    { header: "Modalidades", data: (catalogos.modalidades || []).map(c => c.name || c.label || "") },
    { header: "Tecnología", data: TECNOLOGIA_OPCIONES },
    { header: "Colocación Desechable", data: COLOCACION_DESECHABLE_OPCIONES },
    { header: "Coloca", data: COLOCA_OPCIONES },
  ];

  // Escribir headers y datos en hoja Catálogos
  catColumnas.forEach((col, colIdx) => {
    wsCat.getCell(1, colIdx + 1).value = col.header;
    wsCat.getCell(1, colIdx + 1).font = { bold: true };
    wsCat.getColumn(colIdx + 1).width = 30;
    col.data.forEach((val, rowIdx) => {
      wsCat.getCell(rowIdx + 2, colIdx + 1).value = val;
    });
  });

  // --- Hoja Datos ---
  const wsData = wb.addWorksheet("Datos");

  // Headers con estilo
  TODAS_COLUMNAS.forEach((col, idx) => {
    const cell = wsData.getCell(1, idx + 1);
    cell.value = col;
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: COLUMNAS_OBLIGATORIAS.includes(col) ? "FF2E7D32" : "FF455A64" },
    };
    cell.alignment = { horizontal: "center" };
    wsData.getColumn(idx + 1).width = 24;
  });

  // Mapeo: columna en "Datos" (0-based) → columna en "Catálogos" (1-based) y cantidad de filas
  const dropdownMap = {
    0: { catCol: 8, count: (catalogos.gpsTipoColocaciones || []).length }, // Tipo Colocación GPS
    5: { catCol: 1, count: (catalogos.clientes || []).length },            // Cliente
    7: { catCol: 3, count: (catalogos.movimientosTipo || []).length },     // Tipo Movimiento
    8: { catCol: 4, count: (catalogos.corredores || []).length },          // Corredor/Origen
    15: { catCol: 5, count: (catalogos.localidades || []).length },        // Localidad GPS
    16: { catCol: 2, count: (catalogos.transportistas || []).length },     // Transportista
    17: { catCol: 7, count: (catalogos.contenedoresTipo || []).length },   // Tipo Contenedor
    18: { catCol: 6, count: (catalogos.remolquesTipo || []).length },      // Tamaño Contenedor
  };

  // Opciones fijas (lista directa)
  const fixedDropdownMap = {
    1: TECNOLOGIA_OPCIONES,             // Tecnología
    2: COLOCACION_DESECHABLE_OPCIONES,  // Colocación Desechable
    3: COLOCA_OPCIONES,                 // Coloca
  };

  // Aplicar data validation con referencia a hoja Catálogos (filas 2 a 501)
  const maxRows = 501;
  Object.keys(dropdownMap).forEach(colIdxStr => {
    const colIdx = parseInt(colIdxStr);
    const { catCol, count } = dropdownMap[colIdx];
    if (count > 0) {
      const colLetter = getColLetter(catCol);
      const formula = `Catalogos!$${colLetter}$2:$${colLetter}$${count + 1}`;
      for (let row = 2; row <= maxRows; row++) {
        wsData.getCell(row, colIdx + 1).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [formula],
          showErrorMessage: true,
          errorTitle: "Valor inválido",
          error: "Selecciona un valor de la lista",
        };
      }
    }
  });

  // Aplicar data validation con listas fijas
  Object.keys(fixedDropdownMap).forEach(colIdxStr => {
    const colIdx = parseInt(colIdxStr);
    const options = fixedDropdownMap[colIdx];
    const formulaStr = `"${options.join(",")}"`;
    for (let row = 2; row <= maxRows; row++) {
      wsData.getCell(row, colIdx + 1).dataValidation = {
        type: "list",
        allowBlank: true,
        formulae: [formulaStr],
        showErrorMessage: true,
        errorTitle: "Valor inválido",
        error: "Selecciona un valor de la lista",
      };
    }
  });

  // Nota en columna Modalidades (idx 40 → col 41)
  wsData.getCell(1, 44).note = "Separar por coma. Ej: Carretero, Marítimo";

  // Nota en columnas de fechas (Cita Programada idx 19 → col 20, Cita Colocación idx 20 → col 21)
  wsData.getCell(1, 20).note = "Formato: yyyy-MM-dd HH:mm:ss\nEj: 2026-01-15 08:30:00";
  wsData.getCell(1, 21).note = "Formato: yyyy-MM-dd HH:mm:ss\nEj: 2026-01-15 10:00:00";

  // --- Fila de ejemplo (fila 2) ---
  const ej = {
    "Tipo Colocación GPS": (catalogos.gpsTipoColocaciones || [])[0]?.label || "Principal/Portátil/Recuperable",
    "Tecnología": "",
    "Colocación Desechable": "",
    "Coloca": "",
    "OV": "",
    "Cliente": (catalogos.clientes || [])[0]?.label || "EMPRESA EJEMPLO",
    "Razón Social a Facturar": "EMPRESA FACTURACION S.A. DE C.V.",
    "Tipo Movimiento": (catalogos.movimientosTipo || [])[0]?.label || "Importación",
    "Corredor/Origen": (catalogos.corredores || [])[0]?.label || "MANZANILLO",
    "Estado": "JALISCO",
    "Municipio": "GUADALAJARA",
    "Colonia": "CENTRO",
    "Calle": "AV JUAREZ",
    "Número": "123",
    "CP": "44100",
    "Localidad GPS": (catalogos.localidades || [])[0]?.label || "GUADALAJARA",
    "Transportista": (catalogos.transportistas || [])[0]?.label || "TRANSPORTES EJEMPLO",
    "Tipo Contenedor": (catalogos.contenedoresTipo || [])[0]?.label || "CONTENEDOR",
    "Tamaño Contenedor": (catalogos.remolquesTipo || [])[0]?.label || "CHASIS ABATIBLE",
    "Cita Programada": "2026-01-15 08:30:00",
    "Cita Colocación GPS": "2026-01-15 10:00:00",
    "Modelo Tracto": "",
    "Marca Tractor": "",
    "Color Tractor": "",
    "Placa Tractor": "",
    "Económico Tracto": "",
    "Placa Remolque": "",
    "Económico Remolque": "",
    "Observaciones": "",
    "Nombre Operador": "",
    "Medio Contacto Operador": "",
    "Licencia": "",
    "Booking": "BKG123456",
    "Contacto Monitoreo 1": "JUAN PEREZ - 3312345678",
    "Contacto Monitoreo 2": "MARIA LOPEZ - 3398765432",
    "Contacto Monitoreo 3": "",
    "Contacto Línea Colocación 1": "CARLOS RUIZ - 3376543210",
    "Contacto Línea Colocación 2": "ANA MARTINEZ - 3365432109",
    "Contacto Línea Colocación 3": "",
    "Nota": "",
    "Cuenta Espejo URL": "",
    "Cuenta Espejo Usuario": "",
    "Cuenta Espejo Password": "",
    "Modalidades Transporte": (catalogos.modalidades || [])[0]?.name || "Carretero",
    "Contenedor/Caja": "MSKU1234567",
    "Referencia Facturación": "REF-001",
    "PO/OP": "",
  };

  TODAS_COLUMNAS.forEach((col, idx) => {
    const cell = wsData.getCell(2, idx + 1);
    cell.value = ej[col] || "";
  });

  // Generar y descargar
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Template_Carga_Masiva_Solicitudes.xlsx";
  a.click();
  window.URL.revokeObjectURL(url);
}

// Helper: número de columna (1-based) a letra de Excel
function getColLetter(colNum) {
  let letter = "";
  let num = colNum;
  while (num > 0) {
    const mod = (num - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    num = Math.floor((num - 1) / 26);
  }
  return letter;
}

// ===================== PARSEO Y VALIDACIÓN =====================

// Campos SIEMPRE obligatorios
const CAMPOS_SIEMPRE_OBLIGATORIOS = [
  "Tipo Colocación GPS",
  "Cliente",
  "Razón Social a Facturar",
  "Tipo Movimiento",
  "Corredor/Origen",
  "Estado",
  "Municipio",
  "Colonia",
  "Calle",
  "Número",
  "CP",
  "Localidad GPS",
  "Transportista",
  "Tipo Contenedor",
  "Tamaño Contenedor",
  "Cita Programada",
  "Cita Colocación GPS",
  "Contacto Monitoreo 1",
  "Contacto Monitoreo 2",
  "Contacto Línea Colocación 1",
  "Contacto Línea Colocación 2",
  "Contenedor/Caja",
  "Referencia Facturación",
  "Modalidades Transporte",
];

function parsearYValidar(file, catalogos, solicitudesExistentes) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: "array", cellDates: true });
        // Leer específicamente la hoja "Datos"
        const sheetName = workbook.SheetNames.find(n => n === "Datos") || workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // Parsear usando los headers del propio archivo (fila 1)
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const contenedoresVistos = new Set();
        const resultados = [];

        json.forEach((row, idx) => {
          // Ignorar filas completamente vacías
          const tieneAlgunDato = TODAS_COLUMNAS.some(col => String(row[col] || "").trim().length > 0);
          if (!tieneAlgunDato) return;

          const errores = [];
          const fila = idx + 2; // fila en excel (1-indexed + header)

          // Validar campos SIEMPRE obligatorios
          CAMPOS_SIEMPRE_OBLIGATORIOS.forEach(col => {
            const val = String(row[col] || "").trim();
            if (!val) errores.push(`Campo vacío: ${col}`);
          });

          // Resolver catálogos
          const findInCatalog = (valor, catalogo, nombreCat) => {
            if (!valor) return null;
            const trimVal = String(valor).trim().toLowerCase();
            const found = (catalogo || []).find(
              c => (c.label || "").trim().toLowerCase() === trimVal
            );
            if (!found) errores.push(`${nombreCat}: "${valor}" no encontrado`);
            return found || null;
          };

          const cliente = findInCatalog(row["Cliente"], catalogos.clientes, "Cliente");
          const transportista = findInCatalog(row["Transportista"], catalogos.transportistas, "Transportista");
          const movTipo = findInCatalog(row["Tipo Movimiento"], catalogos.movimientosTipo, "Tipo Movimiento");
          const opTipo = null; // Siempre DESPACHO (id: 2) para plataformas [4,61,1381,1480]
          const corredor = findInCatalog(row["Corredor/Origen"], catalogos.corredores, "Corredor/Origen");
          const localidad = findInCatalog(row["Localidad GPS"], catalogos.localidades, "Localidad GPS");
          const tamContenedor = findInCatalog(row["Tamaño Contenedor"], catalogos.remolquesTipo, "Tamaño Contenedor");
          const tipoContenedor = findInCatalog(row["Tipo Contenedor"], catalogos.contenedoresTipo, "Tipo Contenedor");
          const tipoColocacion = findInCatalog(row["Tipo Colocación GPS"], catalogos.gpsTipoColocaciones, "Tipo Colocación GPS");

          // Validar modalidades
          const modalidadesStr = String(row["Modalidades Transporte"] || "").trim();
          let modalidadesResolved = [];
          if (modalidadesStr) {
            const nombres = modalidadesStr.split(",").map(n => n.trim());
            nombres.forEach(nombre => {
              if (!nombre) return;
              const found = (catalogos.modalidades || []).find(
                m => (m.name || m.label || "").trim().toLowerCase() === nombre.toLowerCase()
              );
              if (!found) {
                errores.push(`Modalidad: "${nombre}" no encontrada`);
              } else {
                modalidadesResolved.push(found.id || found.value);
              }
            });
          }

          // Contenedor duplicado (dentro del archivo)
          const contenedor = String(row["Contenedor/Caja"] || "").trim().toUpperCase();
          if (contenedor) {
            if (contenedoresVistos.has(contenedor)) {
              errores.push(`Contenedor duplicado en archivo: "${contenedor}"`);
            } else {
              contenedoresVistos.add(contenedor);
            }
            // Validar contra solicitudes existentes en el servidor
            if (solicitudesExistentes && solicitudesExistentes.length > 0) {
              const existeEnServidor = solicitudesExistentes.some(sol => {
                if (!sol.contenedor || sol.cancelado) return false;
                const contenedoresSol = sol.contenedor.split(";").map(c => c.trim().toUpperCase());
                return contenedoresSol.includes(contenedor);
              });
              if (existeEnServidor) {
                errores.push(`Contenedor "${contenedor}" ya existe en solicitudes activas`);
              }
            }
          }

          // Validar fechas
          const validarFecha = (valor, nombre) => {
            if (!valor) return "";
            if (valor instanceof Date) return formatDate(valor);
            const strVal = String(valor).trim();
            const regex = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}$/;
            if (!regex.test(strVal)) {
              errores.push(`${nombre}: formato inválido (yyyy-MM-dd HH:mm:ss)`);
              return strVal;
            }
            return strVal;
          };

          const citaProgramada = validarFecha(row["Cita Programada"], "Cita Programada");
          const citaColocacionGps = validarFecha(row["Cita Colocación GPS"], "Cita Colocación GPS");

          // Validar opciones fijas
          const tecnologia = String(row["Tecnología"] || "").trim() || null;
          const colocacionDesechable = String(row["Colocación Desechable"] || "").trim() || null;
          const coloca = String(row["Coloca"] || "").trim() || null;

          if (tecnologia && !TECNOLOGIA_OPCIONES.includes(tecnologia))
            errores.push(`Tecnología: "${tecnologia}" no válida`);
          if (colocacionDesechable && !COLOCACION_DESECHABLE_OPCIONES.includes(colocacionDesechable))
            errores.push(`Colocación Desechable: "${colocacionDesechable}" no válida`);
          if (coloca && !COLOCA_OPCIONES.includes(coloca))
            errores.push(`Coloca: "${coloca}" no válido`);

          // Reglas condicionales
          const tipoColocacionId = tipoColocacion ? (tipoColocacion.value || tipoColocacion.id) : null;
          const esDesechable = tipoColocacionId === 2;
          const esCuentaEspejo = tipoColocacionId === 7;
          const esColocaCliente = coloca === "Coloca cliente";

          if (esDesechable) {
            if (!tecnologia) errores.push("Tecnología requerida (Tipo Colocación Desechable)");
            if (!colocacionDesechable) errores.push("Colocación Desechable requerida");
            if (!coloca) errores.push("Coloca requerido (Tipo Colocación Desechable)");
          }

          if (esColocaCliente) {
            if (!String(row["OV"] || "").trim()) errores.push("OV requerido (Coloca cliente)");
          }

          if (esCuentaEspejo || esColocaCliente) {
            const camposReq = ["Modelo Tracto", "Marca Tractor", "Color Tractor", "Placa Tractor", "Económico Tracto", "Placa Remolque", "Económico Remolque", "Nombre Operador", "Medio Contacto Operador"];
            camposReq.forEach(c => {
              if (!String(row[c] || "").trim()) errores.push(`${c} requerido`);
            });
          }

          resultados.push({
            fila,
            datos: row,
            errores,
            valido: errores.length === 0,
            _resolved: {
              cliente, transportista, movTipo, opTipo, corredor,
              localidad, tamContenedor, tipoContenedor, tipoColocacion,
              modalidadesResolved, citaProgramada, citaColocacionGps,
              tecnologia, colocacionDesechable, coloca,
            }
          });
        });

        resolve(resultados);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Error al leer el archivo"));
    reader.readAsArrayBuffer(file);
  });
}

function formatDate(d) {
  if (!d) return "";
  if (typeof d === "string") return d;
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// ===================== CONSTRUIR JSON PARA ENVÍO =====================
function construirPayload(fila, idPlataforma) {
  const r = fila._resolved;
  const row = fila.datos;

  const destino = [
    String(row["Estado"] || "").trim(),
    String(row["Municipio"] || "").trim(),
    String(row["Colonia"] || "").trim(),
    String(row["Calle"] || "").trim(),
    String(row["Número"] || "").trim(),
    String(row["CP"] || "").trim(),
  ].join(", ");

  const solicitud = {
    idPlataforma: { id: idPlataforma },
    idNaviera: { id: 1 },
    idCliente: r.cliente ? { id: r.cliente.value } : null,
    idTransportista: r.transportista ? { id: r.transportista.value } : null,
    idMovimientoTipo: r.movTipo ? { id: r.movTipo.value } : null,
    idOperacionTipo: { id: 2 }, // DESPACHO - siempre fijo para plataformas [4,61,1381,1480]
    idCorredor: r.corredor ? { id: r.corredor.value } : null,
    idLocalidad: r.localidad ? { id: r.localidad.value } : null,
    idGgpsTipoColocacion: r.tipoColocacion ? { id: r.tipoColocacion.value } : null,
    destino: destino,
    contenedor: String(row["Contenedor/Caja"] || "").trim().toUpperCase(),
    contenedorTipo: r.tipoContenedor ? r.tipoContenedor.value : 1,
    contenedorSize: r.tamContenedor ? r.tamContenedor.value : 0,
    booking: String(row["Booking"] || "").trim().toUpperCase(),
    po: String(row["PO/OP"] || "").trim().toUpperCase(),
    nota: String(row["Nota"] || "").trim(),
    citaProgramada: r.citaProgramada || null,
    citaColocacionGps: r.citaColocacionGps || null,
    vip: true,
    variables: JSON.stringify({ 
      referencia_facturacion: String(row["Referencia Facturación"] || "").trim().toUpperCase(),
      cuenta_espejo: {
        url: String(row["Cuenta Espejo URL"] || "").trim(),
        usuario: String(row["Cuenta Espejo Usuario"] || "").trim(),
        password: String(row["Cuenta Espejo Password"] || "").trim(),
      }
    }),
    clienteS: "null",
    transportistaS: "null",
    tecnologia: r.tecnologia || null,
    colocacionDesechable: r.colocacionDesechable || null,
    coloca: r.coloca || null,
    ov: String(row["OV"] || "").trim().toUpperCase() || null,
    contactoMonitoreo1: String(row["Contacto Monitoreo 1"] || "").trim().toUpperCase(),
    contactoMonitoreo2: String(row["Contacto Monitoreo 2"] || "").trim().toUpperCase(),
    contactoMonitoreo3: String(row["Contacto Monitoreo 3"] || "").trim().toUpperCase(),
    contactoLineaColocacion1: String(row["Contacto Línea Colocación 1"] || "").trim().toUpperCase(),
    contactoLineaColocacion2: String(row["Contacto Línea Colocación 2"] || "").trim().toUpperCase(),
    contactoLineaColocacion3: String(row["Contacto Línea Colocación 3"] || "").trim().toUpperCase(),
    razonSocialFacturar: String(row["Razón Social a Facturar"] || "").trim().toUpperCase(),
  };

  return {
    solicitud,
    modalidades: r.modalidadesResolved || [],
  };
}

// ===================== COMPONENTE PRINCIPAL =====================
const CargaMasivaSolicitudes = ({ catalogos, idPlataforma, plataformas, onClose, onSuccess }) => {
  const [paso, setPaso] = useState(1);
  const [resultados, setResultados] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [resultadoEnvio, setResultadoEnvio] = useState(null);
  const [progreso, setProgreso] = useState(null);
  const [plataformaSeleccionada, setPlataformaSeleccionada] = useState(
    idPlataforma ? { value: idPlataforma } : null
  );
  const [catalogosCargados, setCatalogosCargados] = useState(catalogos || {});
  const [cargandoCatalogos, setCargandoCatalogos] = useState(false);
  const [tabActiva, setTabActiva] = useState("carga"); // "carga" o "progreso"

  const validas = resultados.filter(r => r.valido);
  const invalidas = resultados.filter(r => !r.valido);

  // Recuperar proceso pendiente al montar el componente
  useEffect(() => {
    const idProcesoGuardado = sessionStorage.getItem("bulkProcesoId");
    if (idProcesoGuardado) {
      setTabActiva("progreso");
      recuperarProceso(idProcesoGuardado);
    }
  }, []);

  const recuperarProceso = async (idProceso) => {
    try {
      const token = sessionStorage.getItem("tok");
      const statusRes = await Axios.get(
        api_raiz + "solicitudes_cliente/bulk/status/" + idProceso,
        { headers: { Authorization: "Bearer " + token } }
      );
      const status = statusRes.data;

      if (status.estado === "PROCESANDO") {
        // Retomar polling
        setEnviando(true);
        setProgreso(status);
        setPaso(4);
        const totalGuardado = parseInt(sessionStorage.getItem("bulkTotalEnviadas") || "0");
        iniciarPolling(idProceso, totalGuardado);
      } else if (status.estado === "COMPLETADO") {
        // Mostrar resultado
        setResultadoEnvio({
          exitosas: status.exitosas || 0,
          fallidas: status.fallidas || [],
          total: status.total || 0,
        });
        setProgreso(status);
        setPaso(4);
        sessionStorage.removeItem("bulkProcesoId");
      }
    } catch (err) {
      // Proceso no encontrado — probablemente ya terminó y se limpió del servidor
      sessionStorage.removeItem("bulkProcesoId");
      const totalGuardado = parseInt(sessionStorage.getItem("bulkTotalEnviadas") || "0");
      sessionStorage.removeItem("bulkTotalEnviadas");
      if (err.response && err.response.status === 404) {
        if (totalGuardado > 0) {
          // Sabemos cuántas se enviaron, asumir que terminó bien
          setResultadoEnvio({
            exitosas: totalGuardado,
            fallidas: [],
            total: totalGuardado,
          });
          NotificationManager.success(
            `El proceso finalizó correctamente. Se procesaron ${totalGuardado} solicitudes.`,
            "Carga completada"
          );
          if (onSuccess) {
            const esperarCompletoRecuperar = async () => {
              const maxIntentos = 30;
              let intento = 0;
              const token = sessionStorage.getItem("tok");
              
              while (intento < maxIntentos) {
                await new Promise(r => setTimeout(r, 2000));
                intento++;
                try {
                  const res = await Axios.get(api_raiz + "solicitudes_cliente", { headers: { Authorization: "Bearer " + token } });
                  if (onSuccess) onSuccess();
                  const ahora = new Date().getTime();
                  const recientes = (res.data || []).filter(s => s.creationDate && (ahora - new Date(s.creationDate).getTime()) < 120000);
                  if (recientes.length >= totalGuardado) break;
                } catch (err) {
                  if (onSuccess) onSuccess();
                }
              }
              NotificationManager.success(`Proceso completado. Se procesaron ${totalGuardado} solicitudes.`, "Carga finalizada");
            };
            esperarCompletoRecuperar();
          }
        } else {
          setResultadoEnvio({
            exitosas: 0,
            fallidas: [{ fila: 0, error: "El proceso ya finalizó. Revisa la tabla de solicitudes." }],
            total: 0,
          });
          NotificationManager.info("El proceso de carga ya finalizó previamente. Revisa la tabla de solicitudes.", "Proceso finalizado");
          if (onSuccess) onSuccess();
        }
      }
    }
  };

  const iniciarPolling = (idProceso, totalEnviadas = 0) => {
    const token = sessionStorage.getItem("tok");
    let erroresConsecutivos = 0;
    const MAX_ERRORES = 3; // Después de 3 errores seguidos, detener
    let ultimaProcesada = 0;
    let sinCambioCount = 0;
    const MAX_SIN_CAMBIO = 18; // 18 * 5s = 90 segundos sin avance → alerta

    const intervalo = setInterval(async () => {
      try {
        const statusRes = await Axios.get(
          api_raiz + "solicitudes_cliente/bulk/status/" + idProceso,
          { headers: { Authorization: "Bearer " + token } }
        );
        const status = statusRes.data;
        setProgreso(status);
        erroresConsecutivos = 0; // Reset errores si responde bien

        // Detectar si el proceso se estancó
        if (status.procesadas === ultimaProcesada && status.estado === "PROCESANDO") {
          sinCambioCount++;
          if (sinCambioCount >= MAX_SIN_CAMBIO) {
            clearInterval(intervalo);
            setEnviando(false);
            setResultadoEnvio({
              exitosas: status.exitosas || 0,
              fallidas: [...(status.fallidas || []), { fila: 0, error: "El proceso parece haberse estancado. Revisa las solicitudes creadas." }],
              total: status.total || 0,
            });
            sessionStorage.removeItem("bulkProcesoId");
            NotificationManager.warning(
              `El proceso se detuvo en ${status.procesadas} de ${status.total}. Revisa las solicitudes creadas.`,
              "Proceso estancado"
            );
            if (onSuccess) onSuccess();
            return;
          }
        } else {
          sinCambioCount = 0;
          ultimaProcesada = status.procesadas;
        }

        // Refrescar tabla de solicitudes cada polling para ver las nuevas
        if (onSuccess && status.procesadas > 0) onSuccess();

        if (status.estado === "COMPLETADO") {
          clearInterval(intervalo);
          setResultadoEnvio({
            exitosas: status.exitosas || 0,
            fallidas: status.fallidas || [],
            total: status.total || 0,
          });
          setEnviando(false);
          sessionStorage.removeItem("bulkProcesoId");
          sessionStorage.removeItem("bulkTotalEnviadas");
          if (status.exitosas > 0) {
            NotificationManager.success(`Se crearon ${status.exitosas} solicitudes correctamente`);
          }
          if (onSuccess) {
            const totalCompletado = status.total || totalEnviadas;
            const esperarCompletoPolling = async () => {
              const maxIntentos = 30;
              let intento = 0;
              const token = sessionStorage.getItem("tok");
              
              while (intento < maxIntentos) {
                await new Promise(r => setTimeout(r, 2000));
                intento++;
                try {
                  const res = await Axios.get(api_raiz + "solicitudes_cliente", { headers: { Authorization: "Bearer " + token } });
                  if (onSuccess) onSuccess();
                  const ahora = new Date().getTime();
                  const recientes = (res.data || []).filter(s => s.creationDate && (ahora - new Date(s.creationDate).getTime()) < 120000);
                  if (recientes.length >= totalCompletado) break;
                } catch (err) {
                  if (onSuccess) onSuccess();
                }
              }
            };
            esperarCompletoPolling();
          }
        }
      } catch (err) {
        erroresConsecutivos++;
        console.error(`Error en polling (intento ${erroresConsecutivos}/${MAX_ERRORES}):`, err);

        if (err.response && err.response.status === 404) {
          // Proceso no existe — puede ser que ya terminó y se limpió del servidor
          clearInterval(intervalo);
          setEnviando(false);
          sessionStorage.removeItem("bulkProcesoId");
          
          // Si ya teníamos progreso previo O sabemos cuántas se enviaron, asumir éxito
          if (ultimaProcesada > 0 || totalEnviadas > 0) {
            const exitosas = ultimaProcesada > 0 ? ultimaProcesada : totalEnviadas;
            // Mostrar mensaje intermedio mientras se refrescan los datos
            setProgreso({ total: exitosas, procesadas: exitosas, exitosas: exitosas, fallidas: [], estado: "FINALIZANDO" });
            
            if (onSuccess) {
              // Refresco inteligente: consulta hasta que la tabla tenga todas las solicitudes
              const esperarCompleto = async () => {
                const maxIntentos = 30; // máximo 30 intentos (60 segundos)
                let intento = 0;
                const token = sessionStorage.getItem("tok");
                
                while (intento < maxIntentos) {
                  await new Promise(r => setTimeout(r, 2000)); // esperar 2s entre cada consulta
                  intento++;
                  try {
                    const res = await Axios.get(
                      api_raiz + "solicitudes_cliente",
                      { headers: { Authorization: "Bearer " + token } }
                    );
                    const solicitudesActuales = res.data || [];
                    if (onSuccess) onSuccess(); // refrescar tabla
                    
                    // Contar solicitudes nuevas (creadas en los últimos 2 minutos)
                    const ahora = new Date().getTime();
                    const recientes = solicitudesActuales.filter(s => {
                      if (!s.creationDate) return false;
                      const creada = new Date(s.creationDate).getTime();
                      return (ahora - creada) < 120000; // últimos 2 minutos
                    });
                    
                    if (recientes.length >= exitosas) {
                      // Ya están todas
                      break;
                    }
                  } catch (err) {
                    // Si falla la consulta, seguir intentando
                    if (onSuccess) onSuccess();
                  }
                }
                
                // Mostrar resultado final
                setResultadoEnvio({
                  exitosas: exitosas,
                  fallidas: [],
                  total: exitosas,
                });
                setProgreso(null);
                NotificationManager.success(
                  `Proceso completado. Se procesaron ${exitosas} solicitudes.`,
                  "Carga finalizada"
                );
              };
              esperarCompleto();
            }
          } else {
            setProgreso(null);
            setResultadoEnvio({
              exitosas: 0,
              fallidas: [{ fila: 0, error: "El proceso ya no existe en el servidor. Posiblemente expiró o fue completado previamente." }],
              total: 0,
            });
            NotificationManager.warning("El proceso de carga ya no existe en el servidor", "Proceso no encontrado");
          }
        } else if (erroresConsecutivos >= MAX_ERRORES) {
          // Demasiados errores de red seguidos — detener
          clearInterval(intervalo);
          setEnviando(false);
          sessionStorage.removeItem("bulkProcesoId");
          NotificationManager.error(
            "Se perdió la conexión con el servidor. Revisa las solicitudes creadas manualmente.",
            "Error de conexión"
          );
          setResultadoEnvio({
            exitosas: 0,
            fallidas: [{ fila: 0, error: "Se perdió la conexión después de múltiples intentos. Verifica manualmente las solicitudes creadas." }],
            total: 0,
          });
        }
        // Si no alcanza MAX_ERRORES, sigue intentando en el próximo intervalo
      }
    }, 5000); // Polling cada 5 segundos
  };

  // Cargar catálogos al seleccionar plataforma
  const cargarCatalogos = async (idPlt) => {
    setCargandoCatalogos(true);
    try {
      const token = sessionStorage.getItem("tok");
      const headers = { Authorization: "Bearer " + token };
      const user = JSON.parse(sessionStorage.getItem("usuario"));

      const [
        clientesRes, transportistasRes, corredoresRes, localidadesRes,
        localidadesMTRes, movTiposRes, opTiposRes, gpsTipoRes,
        contenedorTipoRes, remolquesTipoRes, modalidadesRes
      ] = await Promise.all([
        Axios.get(api_raiz + "cliente/cli_usuario/" + idPlt, { headers }),
        Axios.get(api_raiz + "transportistas/cli_usuario/" + idPlt, { headers }),
        Axios.get(api_raiz + "corredores/cli_usuario/" + idPlt, { headers }),
        Axios.get(api_raiz + "localidades/cli_usuario/" + idPlt, { headers }),
        Axios.get(api_raiz + "localidades/mt", { headers }),
        Axios.get(api_raiz + "movimientosTipo/cli_usuario/" + idPlt, { headers }),
        Axios.get(api_raiz + "operacionesTipo/plataforma/" + idPlt, { headers }),
        Axios.get(api_raiz + "gpsTipoColocacion", { headers }),
        Axios.get(api_raiz + "contenedorTipo", { headers }),
        Axios.get(api_raiz + "remolquesTipo", { headers }),
        Axios.get(api_raiz + "modalidades-transporte", { headers }),
      ]);

      const gpsFiltrado = (gpsTipoRes.data || []).filter(gt => gt.id !== 6 && gt.id <= 7);

      setCatalogosCargados({
        clientes: (clientesRes.data || []).map(c => ({ value: c.id, label: c.nombre, data: c })),
        transportistas: (transportistasRes.data || []).map(t => ({ value: t.id, label: t.nombre, data: t })),
        corredores: (corredoresRes.data || []).map(c => ({ value: c.id, label: c.nombre, data: c })),
        localidades: (localidadesMTRes.data && localidadesMTRes.data.length > 0
          ? localidadesMTRes.data
          : localidadesRes.data || []
        ).map(l => ({ value: l.id || l.idLocalidadmt, label: l.nombre, data: l })),
        movimientosTipo: (movTiposRes.data || []).map(m => ({ value: m.id, label: m.nombre, data: m })),
        operacionTipos: (opTiposRes.data || []).map(o => ({ value: o.id, label: o.nombre, data: o })),
        gpsTipoColocaciones: gpsFiltrado.map(g => ({
          value: g.id,
          label: g.nombre === "Respaldo" ? "Doble GPS" : g.nombre === "Principal" ? "Principal/Portátil/Recuperable" : g.nombre,
          data: g,
        })),
        contenedoresTipo: (contenedorTipoRes.data || []).map(c => ({ value: c.id, label: c.nombre, data: c })),
        remolquesTipo: (remolquesTipoRes.data || []).map(r => ({ value: r.idRemolque, label: r.nombre, data: r })),
        modalidades: (modalidadesRes.data || []).sort((a, b) => {
          if (a.name === "Carretero") return -1;
          if (b.name === "Carretero") return 1;
          return a.name.localeCompare(b.name);
        }),
      });

      NotificationManager.success("Catálogos cargados correctamente");
    } catch (err) {
      console.error("Error cargando catálogos:", err);
      NotificationManager.error("Error al cargar catálogos: " + err.message);
    } finally {
      setCargandoCatalogos(false);
    }
  };

  const descargarTemplate = async () => {
    const totalCatalogos = (catalogosCargados.clientes || []).length +
                           (catalogosCargados.transportistas || []).length +
                           (catalogosCargados.corredores || []).length;
    if (totalCatalogos === 0) {
      NotificationManager.warning(
        "Los catálogos están vacíos. Selecciona una plataforma y carga los catálogos primero.",
        "Catálogos vacíos"
      );
      return;
    }
    await generarTemplate(catalogosCargados);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      // Obtener solicitudes existentes para validar contenedores duplicados
      const token = sessionStorage.getItem("tok");
      let solicitudesExistentes = [];
      try {
        const solRes = await Axios.get(api_raiz + "solicitudes_cliente", {
          headers: { Authorization: "Bearer " + token }
        });
        solicitudesExistentes = solRes.data || [];
      } catch (err) {
        console.warn("No se pudieron obtener solicitudes existentes:", err);
      }
      const res = await parsearYValidar(file, catalogosCargados, solicitudesExistentes);
      setResultados(res);
      setPaso(3);
    } catch (err) {
      NotificationManager.error("Error al leer el archivo: " + err.message);
    }
  };

  const enviarValidas = async () => {
    if (validas.length === 0) return;
    setEnviando(true);
    setProgreso({ total: validas.length, procesadas: 0, exitosas: 0, fallidas: [], estado: "PROCESANDO" });
    setTabActiva("progreso");
    const idPlt = plataformaSeleccionada ? plataformaSeleccionada.value : idPlataforma;
    const payloads = validas.map(f => construirPayload(f, idPlt));

    try {
      const token = sessionStorage.getItem("tok");
      const response = await Axios.post(
        api_raiz + "solicitudes_cliente/bulk",
        payloads,
        { headers: { Authorization: "Bearer " + token, "Content-Type": "application/json" } }
      );
      const data = response.data;
      const idProceso = data.idProceso;

      if (!idProceso) {
        // Respuesta directa (sin polling) — compatibilidad
        setResultadoEnvio({
          exitosas: data.exitosas || 0,
          fallidas: data.fallidas || [],
          total: data.total || validas.length,
        });
        setEnviando(false);
        return;
      }

      // Guardar en sessionStorage para recuperar si refresca
      sessionStorage.setItem("bulkProcesoId", idProceso);
      sessionStorage.setItem("bulkTotalEnviadas", validas.length.toString());

      // Iniciar polling
      iniciarPolling(idProceso, validas.length);

    } catch (err) {
      const msg = err.response && err.response.data
        ? JSON.stringify(err.response.data)
        : err.message;
      setResultadoEnvio({ exitosas: 0, fallidas: [{ fila: 0, error: msg }], total: validas.length });
      setEnviando(false);
      NotificationManager.error("Error al enviar: " + msg);
    }
  };

  const cancelar = () => {
    setResultados([]);
    setPaso(1);
    setResultadoEnvio(null);
    setProgreso(null);
    sessionStorage.removeItem("bulkProcesoId");
  };

  return (
    <div style={{ padding: "20px", maxHeight: "80vh", overflowY: "auto" }}>
      <h5 style={{ marginBottom: "15px" }}>
        <Icon name="upload" className="mr-2" />
        Carga Masiva de Solicitudes
      </h5>

      {/* ========== PESTAÑAS ========== */}
      <div style={{ display: "flex", borderBottom: "2px solid #dee2e6", marginBottom: "15px" }}>
        <button
          onClick={() => setTabActiva("carga")}
          style={{
            padding: "8px 20px",
            border: "none",
            borderBottom: tabActiva === "carga" ? "3px solid #467fcf" : "3px solid transparent",
            backgroundColor: "transparent",
            fontWeight: tabActiva === "carga" ? "bold" : "normal",
            color: tabActiva === "carga" ? "#467fcf" : "#666",
            cursor: "pointer",
            fontSize: "14px",
          }}
        >
          <Icon name="file-plus" className="mr-1" /> Nueva Carga
        </button>
        <button
          onClick={() => {
            setTabActiva("progreso");
            // Si hay proceso guardado, recuperar
            const idProcesoGuardado = sessionStorage.getItem("bulkProcesoId");
            if (idProcesoGuardado && !enviando) {
              recuperarProceso(idProcesoGuardado);
            }
          }}
          style={{
            padding: "8px 20px",
            border: "none",
            borderBottom: tabActiva === "progreso" ? "3px solid #467fcf" : "3px solid transparent",
            backgroundColor: "transparent",
            fontWeight: tabActiva === "progreso" ? "bold" : "normal",
            color: tabActiva === "progreso" ? "#467fcf" : "#666",
            cursor: "pointer",
            fontSize: "14px",
            position: "relative",
          }}
        >
          <Icon name="activity" className="mr-1" /> Progreso
          {sessionStorage.getItem("bulkProcesoId") && (
            <span style={{
              position: "absolute",
              top: "4px",
              right: "4px",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: enviando ? "#ffc107" : "#28a745",
              animation: enviando ? "pulse 1.5s infinite" : "none",
            }} />
          )}
        </button>
      </div>

      {/* ========== TAB: NUEVA CARGA ========== */}
      {tabActiva === "carga" && (
        <>
      {/* ========== PASO 1: Plataforma + Catálogos + Template/Subir ========== */}
      {paso === 1 && (
        <div>
          <Alert type="info">
            Selecciona la plataforma, carga los catálogos y luego descarga el template o sube tu archivo directamente.
          </Alert>

          {/* Selector de plataforma */}
          <div style={{ marginBottom: "15px", maxWidth: "400px" }}>
            <label style={{ fontWeight: "bold", marginBottom: "5px", display: "block" }}>Plataforma:</label>
            {plataformas && plataformas.length > 0 ? (
              <select
                className="form-control"
                value={plataformaSeleccionada ? plataformaSeleccionada.value : ""}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  const plt = plataformas.find(p => p.value === val);
                  setPlataformaSeleccionada(plt || { value: val });
                  setCatalogosCargados({});
                }}
              >
                <option value="">-- Selecciona plataforma --</option>
                {plataformas.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            ) : (
              <p style={{ fontSize: "12px", color: "#666" }}>
                Plataforma actual: <strong>{idPlataforma}</strong>
              </p>
            )}
          </div>

          {/* Botón cargar catálogos */}
          <Button
            color="info"
            onClick={() => {
              const idPlt = plataformaSeleccionada ? plataformaSeleccionada.value : idPlataforma;
              if (!idPlt) {
                NotificationManager.warning("Selecciona una plataforma primero");
                return;
              }
              cargarCatalogos(idPlt);
            }}
            disabled={cargandoCatalogos}
            className="mr-2"
          >
            <Icon name="refresh-cw" className="mr-1" />
            {cargandoCatalogos ? "Cargando..." : "Cargar Catálogos"}
          </Button>

          {/* Resumen de catálogos cargados */}
          {(catalogosCargados.clientes || []).length > 0 && (
            <>
              <div style={{ marginTop: "10px", marginBottom: "10px", fontSize: "11px", color: "#28a745" }}>
                ✓ Catálogos cargados: {(catalogosCargados.clientes || []).length} clientes,{" "}
                {(catalogosCargados.transportistas || []).length} transportistas,{" "}
                {(catalogosCargados.corredores || []).length} corredores,{" "}
                {(catalogosCargados.localidades || []).length} localidades,{" "}
                {(catalogosCargados.modalidades || []).length} modalidades
              </div>

              <hr />

              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Button color="primary" onClick={descargarTemplate}>
                  <Icon name="download" className="mr-1" /> Descargar Template
                </Button>

                <span style={{ fontSize: "12px", color: "#666" }}>|</span>

                <div>
                  <label style={{ fontWeight: "bold", fontSize: "12px", marginBottom: "4px", display: "block" }}>
                    Subir archivo:
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ========== PASO 3: Preview ========== */}
      {paso === 3 && (
        <div>
          <Alert type="info">
            <strong>Paso 3:</strong> Vista previa — {" "}
            <span style={{ color: "#28a745" }}><strong>{validas.length}</strong> válidas</span>,{" "}
            <span style={{ color: "#dc3545" }}><strong>{invalidas.length}</strong> con errores</span>.
            {" "}Total: {resultados.length} filas.
          </Alert>

          {invalidas.length > 0 && (
            <Alert type="warning">
              Las filas con errores NO serán enviadas. Corrígelas en el Excel y vuelve a subir, o envía solo las válidas.
            </Alert>
          )}

          <div style={{ overflowX: "auto", maxHeight: "45vh", overflowY: "auto", marginBottom: "15px" }}>
            <table className="table table-sm table-bordered" style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
              <thead style={{ backgroundColor: "#343a40", color: "#fff", position: "sticky", top: 0, zIndex: 1 }}>
                <tr>
                  <th>Fila</th>
                  <th>Estado</th>
                  {TODAS_COLUMNAS.map(col => (
                    <th key={col}>{col}</th>
                  ))}
                  <th>Errores</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r, i) => (
                  <tr key={i} style={{ backgroundColor: r.valido ? "#d4edda" : "#f8d7da" }}>
                    <td>{r.fila}</td>
                    <td>{r.valido ? "✓" : "✗"}</td>
                    {TODAS_COLUMNAS.map(col => (
                      <td key={col}>{String(r.datos[col] || "")}</td>
                    ))}
                    <td style={{ whiteSpace: "normal", maxWidth: "300px", fontSize: "10px", color: "#dc3545" }}>
                      {r.errores.length > 0 ? r.errores.join(" | ") : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ textAlign: "right" }}>
            <Button color="secondary" className="mr-2" onClick={cancelar}>Cancelar</Button>
            <Button
              color="primary"
              onClick={enviarValidas}
              disabled={enviando || validas.length === 0}
            >
              {enviando ? "Enviando..." : `Enviar ${validas.length} válidas`}
            </Button>
          </div>
        </div>
      )}

      {/* Paso 4 ya no se usa aquí, se muestra en tab Progreso */}
        </>
      )}

      {/* ========== TAB: PROGRESO ========== */}
      {tabActiva === "progreso" && (
        <div>
          {/* Sin proceso activo */}
          {!enviando && !progreso && !resultadoEnvio && (
            <div style={{ textAlign: "center", padding: "40px 0", color: "#999" }}>
              <Icon name="inbox" style={{ fontSize: "48px", marginBottom: "10px" }} />
              <p>No hay ningún proceso de carga masiva en curso.</p>
              <Button color="primary" size="sm" onClick={() => setTabActiva("carga")}>
                Ir a Nueva Carga
              </Button>
            </div>
          )}

          {/* Barra de progreso mientras procesa */}
          {enviando && progreso && (
            <div>
              <Alert type="info">
                <strong>Procesando solicitudes...</strong> Puedes cerrar este modal. El proceso continuará en segundo plano.
              </Alert>
              <div style={{ marginBottom: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "5px" }}>
                  <span>Progreso: <strong>{progreso.procesadas || 0}</strong> de <strong>{progreso.total || 0}</strong></span>
                  <span>{progreso.total > 0 ? Math.round(((progreso.procesadas || 0) / progreso.total) * 100) : 0}%</span>
                </div>
                <div style={{ width: "100%", backgroundColor: "#e9ecef", borderRadius: "4px", height: "24px", overflow: "hidden" }}>
                  <div style={{
                    width: `${progreso.total > 0 ? ((progreso.procesadas || 0) / progreso.total) * 100 : 0}%`,
                    backgroundColor: "#28a745",
                    height: "100%",
                    borderRadius: "4px",
                    transition: "width 0.5s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}>
                    {progreso.procesadas > 0 && `${progreso.procesadas}/${progreso.total}`}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: "11px", color: "#666" }}>
                ✓ Exitosas: <strong style={{ color: "#28a745" }}>{progreso.exitosas || 0}</strong>
                {progreso.fallidas && progreso.fallidas.length > 0 && (
                  <span> | ✗ Fallidas: <strong style={{ color: "#dc3545" }}>{progreso.fallidas.length}</strong></span>
                )}
              </div>
              <div style={{ marginTop: "15px" }}>
                {onClose && (
                  <Button color="link" size="sm" onClick={onClose}>Cerrar (el proceso continúa en segundo plano)</Button>
                )}
              </div>
            </div>
          )}

          {/* Resultado final cuando terminó */}
          {!enviando && !resultadoEnvio && progreso && progreso.estado === "FINALIZANDO" && (
            <div>
              <Alert type="success">
                <strong>Finalizando carga...</strong> Las solicitudes se están registrando. La tabla se actualizará automáticamente.
              </Alert>
              <div style={{ marginBottom: "10px" }}>
                <div style={{ width: "100%", backgroundColor: "#e9ecef", borderRadius: "4px", height: "24px", overflow: "hidden" }}>
                  <div style={{
                    width: "100%",
                    backgroundColor: "#28a745",
                    height: "100%",
                    borderRadius: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: "12px",
                    fontWeight: "bold",
                    animation: "pulse 1.5s infinite",
                  }}>
                    Actualizando tabla... ({progreso.total} solicitudes)
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resultado final cuando terminó */}
          {!enviando && resultadoEnvio && (
            <div>
              <Alert type={resultadoEnvio.exitosas > 0 ? "success" : "danger"}>
                <strong>✓ Carga completada</strong><br />
                Solicitudes creadas: <strong>{resultadoEnvio.exitosas}</strong> de {resultadoEnvio.total}
              </Alert>

              {resultadoEnvio.fallidas && resultadoEnvio.fallidas.length > 0 && (
                <Alert type="warning">
                  <strong>Errores ({resultadoEnvio.fallidas.length}):</strong>
                  <ul style={{ marginBottom: 0, paddingLeft: "20px", fontSize: "11px", maxHeight: "200px", overflowY: "auto" }}>
                    {resultadoEnvio.fallidas.map((f, i) => (
                      <li key={i}>{f.fila ? `Fila ${f.fila}: ` : ""}{f.error}</li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div style={{ marginTop: "15px" }}>
                <Button color="secondary" size="sm" onClick={() => { cancelar(); setTabActiva("carga"); }} className="mr-2">
                  Cargar otro archivo
                </Button>
                {onClose && (
                  <Button color="primary" size="sm" onClick={onClose}>Cerrar</Button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CargaMasivaSolicitudes;
